import { LocalStorageSaveAdapter, type SaveAdapter } from "@yjsgames/save";
import { SOUND_CATEGORIES, type SoundCategory } from "@yjsgames/sound";
import {
  type BrickSettings,
  createDefaultBrickSettings,
  DEFAULT_SOUND_VOLUME_LEVEL,
  MAX_VOLUME_LEVEL,
  PERF_OVERLAY_AVAILABLE,
  PERF_OVERLAY_FORCED,
} from "../models/BrickSettings.js";
import { SUPPORTED_LANGUAGES, type LanguageCode } from "../i18n/locales.js";
import type { NumberFormat } from "../models/ModuleBrickSaveData.js";
import { isTargetFpsSetting } from "@yjsgames/perf";

const SETTINGS_KEY = "module-brick-idle-settings-v1";
const DEFAULT_OPERATION_TIMEOUT_MS = 1500;

/**
 * 기기별 설정(사운드 볼륨/언어/표기형식) 전용 저장 — 진행 데이터(ModuleBrickSaveSystem)와 **별도 키로 분리**.
 *
 * 주입된 SaveAdapter가 "저장 위치"를 결정한다 — 부트스트랩에서 **로컬 전용 어댑터**(클라우드 미포함)를 주입하면
 * 설정이 클라우드 동기화에서 제외된다(기기별 설정). idleblade `SettingSaveEntry`(SaveLocation.Local) 패턴의
 * TS 구현: 엔트리 = 별도 키, 위치 = 주입 어댑터.
 */
export class BrickSettingsStore {
  private loadFailed = false;

  constructor(
    private readonly adapter: SaveAdapter = new LocalStorageSaveAdapter(),
    private readonly key: string = SETTINGS_KEY,
    private readonly operationTimeoutMs: number = DEFAULT_OPERATION_TIMEOUT_MS,
  ) {}

  get lastLoadFailed(): boolean {
    return this.loadFailed;
  }

  /**
   * 저장된 설정 로드 — 정규화 후 반환. 저장 없음/파싱 실패 시 null(= GameScene이 구 진행 데이터 마이그레이션 트리거).
   * 손상 JSON은 throw하지 않고 null로 graceful fallback(설정 저장이 깨져도 게임 부팅은 막지 않음).
   */
  async load(): Promise<BrickSettings | null> {
    this.loadFailed = false;
    try {
      const raw = await withTimeout(
        this.adapter.load(this.key),
        this.operationTimeoutMs,
        "settings load timed out",
      );
      if (raw === null) return null;
      return normalizeSettings(JSON.parse(raw));
    } catch (error) {
      this.loadFailed = true;
      console.warn("[BrickSettingsStore] load failed:", (error as Error).message);
      return null;
    }
  }

  /** 설정 저장(JSON 직렬화). 어댑터 오류는 throw하지 않고 warn만 — 설정 저장 실패가 게임 흐름을 끊지 않게. */
  async save(settings: BrickSettings): Promise<void> {
    try {
      await withTimeout(
        this.adapter.save(this.key, JSON.stringify(settings)),
        this.operationTimeoutMs,
        "settings save timed out",
      );
    } catch (error) {
      console.warn("[BrickSettingsStore] save failed:", (error as Error).message);
    }
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<T>((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
  });
}

const NUMBER_FORMATS: readonly NumberFormat[] = ["auto", "kmg", "eastAsian", "alphabet"];

/** 부분/손상 설정 → 기본값 보정 BrickSettings (field-add + 볼륨 범위 clamp + 미지원 언어/포맷 fallback). */
export function normalizeSettings(parsed: unknown): BrickSettings {
  const defaults = createDefaultBrickSettings();
  if (typeof parsed !== "object" || parsed === null) return defaults;
  const p = parsed as Partial<BrickSettings> & Record<string, unknown>;
  return {
    soundVolumes: normalizeVolumes(p.soundVolumes),
    language: isSupportedLanguage(p.language) ? p.language : defaults.language,
    numberFormat:
      typeof p.numberFormat === "string" &&
      (NUMBER_FORMATS as readonly string[]).includes(p.numberFormat)
        ? (p.numberFormat as NumberFormat)
        : defaults.numberFormat,
    targetFps: isTargetFpsSetting(p.targetFps) ? p.targetFps : defaults.targetFps,
    perfOverlayEnabled: PERF_OVERLAY_FORCED
      ? true
      : PERF_OVERLAY_AVAILABLE && typeof p.perfOverlayEnabled === "boolean"
        ? p.perfOverlayEnabled
        : defaults.perfOverlayEnabled,
  };
}

function normalizeVolumes(raw: unknown): Record<SoundCategory, number> {
  const obj = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
  const out = {} as Record<SoundCategory, number>;
  for (const cat of SOUND_CATEGORIES) {
    const v = obj[cat];
    out[cat] =
      typeof v === "number" && Number.isFinite(v) ? clampVolumeLevel(v) : DEFAULT_SOUND_VOLUME_LEVEL;
  }
  return out;
}

function clampVolumeLevel(v: number): number {
  const n = Math.round(v);
  if (n < 0) return 0;
  if (n > MAX_VOLUME_LEVEL) return MAX_VOLUME_LEVEL;
  return n;
}

function isSupportedLanguage(v: unknown): v is LanguageCode {
  return typeof v === "string" && (SUPPORTED_LANGUAGES as readonly string[]).includes(v);
}
